<div class="course-page">
  <div class="page-header">
    <div>
      <p-button
        label="Back to Practice Courses"
        icon="pi pi-arrow-left"
        [text]="true"
        routerLink="/practice-courses"
      />
      <h1>{{ detail()?.name ?? 'Course drilldown' }}</h1>
      @if (detail(); as course) {
        <p>
          {{ course.organisationName }} · Version {{ course.version }} · Published
          {{ course.publishedAt | date: 'mediumDate' }}
        </p>
      }
    </div>
    @if (detail(); as course) {
      <p-tag [value]="course.status" [severity]="severity(course.status)" />
    }
  </div>

  @if (error()) {
    <div class="error-state" role="alert">
      <span>Course drilldown could not be loaded.</span
      ><p-button label="Try again" icon="pi pi-refresh" [outlined]="true" (onClick)="load()" />
    </div>
  }
  @if (courseFreshness()?.status === 'stale') {
    <div class="stale-state" role="status">
      <i class="pi pi-clock"></i> Progress is catching up for
      {{ courseFreshness()!.staleEnrollmentCount }} of
      {{ courseFreshness()!.enrollmentCount }} enrollment{{
        courseFreshness()!.enrollmentCount === 1 ? '' : 's'
      }}
      in this course.
      @if (courseFreshness()!.sourceGeneratedAt) {
        <span
          >Last complete snapshot:
          {{ courseFreshness()!.sourceGeneratedAt | date: 'medium' }}.</span
        >
      }
      <p-button label="Refresh" icon="pi pi-refresh" [text]="true" (onClick)="load()" />
    </div>
  }
  @if (pageFreshness()?.status === 'stale') {
    <div class="stale-state" role="status">
      <i class="pi pi-clock"></i> {{ pageFreshness()!.staleEnrollmentCount }} rep{{
        pageFreshness()!.staleEnrollmentCount === 1 ? '' : 's'
      }}
      on this page {{ pageFreshness()!.staleEnrollmentCount === 1 ? 'is' : 'are' }} still catching
      up.
    </div>
  }
  @if (courseFreshness()?.status === 'unavailable') {
    <div class="unavailable-state" role="status">
      <i class="pi pi-info-circle"></i> Aggregate freshness is unavailable — there is no snapshot
      data to compare against yet.
    </div>
  }
  @if (loading()) {
    <p-card
      ><div class="empty">
        <i class="pi pi-spin pi-spinner"></i> Loading course progress…
      </div></p-card
    >
  }
  @if (detail(); as course) {
    <div class="metric-grid">
      <p-card
        ><span>Assignments</span><strong>{{ totalCount(course.assignmentCounts) }}</strong
        ><small>{{ assignmentSources(course.assignmentSourceCounts) }}</small></p-card
      >
      @for (item of course.readinessDistribution; track item.state) {
        @if (item.count > 0) {
          <p-card
            ><span>{{ label(item.state) }}</span
            ><strong>{{ item.count }}</strong
            ><small>enrollments</small></p-card
          >
        }
      }
    </div>

    <p-card header="Course metadata">
      <div class="metadata-grid">
        <div>
          <span>Objective</span>
          <p>{{ course.objective || '—' }}</p>
        </div>
        <div>
          <span>Description</span>
          <p>{{ course.description || '—' }}</p>
        </div>
        <div>
          <span>Sales motion</span>
          <p>{{ course.salesMotion || '—' }}</p>
        </div>
        <div>
          <span>Target roles</span>
          <p>{{ course.targetRoles.join(', ') || '—' }}</p>
        </div>
      </div>
    </p-card>

    <p-card header="Scenario coverage">
      @for (module of course.scenarioCoverage; track module.moduleId) {
        <h3>{{ module.orderIndex + 1 }}. {{ module.moduleName }}</h3>
        <p-table [value]="module.scenarioRanges" styleClass="p-datatable-sm">
          <ng-template pTemplate="header"
            ><tr>
              <th>Scenario range</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>Passed</th>
              <th>Users covered</th>
            </tr></ng-template
          >
          <ng-template pTemplate="body" let-range
            ><tr>
              <td>{{ range.name }}</td>
              <td><p-tag [value]="range.status" [severity]="severity(range.status)" /></td>
              <td>{{ range.attempts }}</td>
              <td>{{ range.passedAttempts }}</td>
              <td>{{ range.uniqueUsers }}</td>
            </tr></ng-template
          >
        </p-table>
      }
    </p-card>

    <p-card header="Rep progress">
      <p-table
        [value]="reps()"
        [loading]="repLoading()"
        [lazy]="true"
        [paginator]="true"
        [rows]="20"
        [totalRecords]="total()"
        (onLazyLoad)="loadReps($event)"
        [tableStyle]="{ 'min-width': '60rem' }"
      >
        <ng-template pTemplate="header"
          ><tr>
            <th>Rep</th>
            <th>Assignment</th>
            <th>Enrollment</th>
            <th>Readiness</th>
            <th>Module</th>
            <th>Attempts</th>
            <th>Last attempt</th>
            <th></th></tr
        ></ng-template>
        <ng-template pTemplate="body" let-rep
          ><tr class="clickable" (click)="openRep(rep)">
            <td>
              <strong>{{ rep.user.name }}</strong
              ><br /><small>{{ rep.user.email }}</small>
            </td>
            <td>
              {{ rep.assignment.source
              }}{{ rep.assignment.teamName ? ' · ' + rep.assignment.teamName : '' }}
            </td>
            <td><p-tag [value]="rep.status" [severity]="severity(rep.status)" /></td>
            <td>
              <p-tag
                [value]="label(rep.readinessState)"
                [severity]="severity(rep.readinessState)"
              /><br /><small>{{ percent(rep.readinessScore) }}%</small>
            </td>
            <td>{{ rep.currentModule?.name || '—' }}</td>
            <td>{{ rep.passedAttemptCount }} / {{ rep.attemptCount }} passed</td>
            <td>{{ rep.lastAttemptAt ? (rep.lastAttemptAt | date: 'medium') : '—' }}</td>
            <td><i class="pi pi-chevron-right"></i></td></tr
        ></ng-template>
        <ng-template pTemplate="emptymessage"
          ><tr>
            <td colspan="8" class="empty">No enrolled reps.</td>
          </tr></ng-template
        >
      </p-table>
    </p-card>
  }
</div>
